import React from "react";

import { AuthenticateStep } from "./AuthenticateStep";
import { CrawlerPreferenceStep } from "./CrawlerPreferenceStep";
import { LoadingStep } from "./LoadingStep";
import { ManageStep } from "./ManageStep";
import { PolishedAuthenticateStep } from "./PolishedAuthenticateStep";
import { PolishedManageStep } from "./PolishedManageStep";
import { ResultStep } from "./ResultStep";

import type { IntegrationScope } from "../constants";
import type { Integration } from "../types/integrations";
import type { IntegrationModalAppearance, ModalStep } from "../types/modal";

interface IntegrationModalContentProps {
  integration: Integration;
  integrationScope?: IntegrationScope;
  currentStep: ModalStep;
  isStepLoading: boolean;
  selectedAccountId?: string;
  isResultSuccess: boolean;
  errorMessage: string | null;
  fromCrawlerPreference: boolean;
  showNonOAuthForm?: boolean;
  onConnect: (accountId?: string) => void;
  onCrawlerPreferenceNext: () => void;
  onCrawlerPreferenceError: (error?: string) => void;
  onManage: (accountId?: string) => void;
  onClose: () => void;
  onSetAccountId?: (accountId: string) => void;
  appearance?: IntegrationModalAppearance;
}

export function IntegrationModalContent({
  integration,
  integrationScope,
  currentStep,
  isStepLoading,
  selectedAccountId,
  isResultSuccess,
  errorMessage,
  fromCrawlerPreference,
  showNonOAuthForm,
  onConnect,
  onCrawlerPreferenceNext,
  onCrawlerPreferenceError,
  onManage,
  onClose,
  onSetAccountId,
  appearance = "default",
}: IntegrationModalContentProps) {
  const isPolished = appearance === "polished";
  const commonProps = {
    integration,
    integrationScope,
    onClose,
    isLoading: isStepLoading,
    selectedAccountId,
  };

  if (isStepLoading && currentStep !== "result") {
    return <LoadingStep integration={integration} />;
  }

  switch (currentStep) {
    case "authenticate":
      if (isPolished && !showNonOAuthForm) {
        return (
          <PolishedAuthenticateStep {...commonProps} onConnect={onConnect} />
        );
      }
      return (
        <AuthenticateStep
          {...commonProps}
          onConnect={onConnect}
          onSetAccountId={onSetAccountId}
          showNonOAuthForm={showNonOAuthForm}
        />
      );
    case "crawler_preference":
      return (
        <CrawlerPreferenceStep
          {...commonProps}
          onNext={onCrawlerPreferenceNext}
          onError={onCrawlerPreferenceError}
        />
      );
    case "result":
      return (
        <ResultStep
          {...commonProps}
          onConnect={onConnect}
          success={isResultSuccess}
          errorMessage={errorMessage}
          fromCrawlerPreference={fromCrawlerPreference}
        />
      );
    case "manage":
      if (isPolished) {
        return (
          <PolishedManageStep
            {...commonProps}
            onConnect={onConnect}
            onManage={onManage}
          />
        );
      }
      return (
        <ManageStep
          {...commonProps}
          onConnect={onConnect}
          onManage={onManage}
        />
      );
    default:
      return null;
  }
}
